import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import {
  concat,
  merge,
  Observable,
  shareReplay,
  distinctUntilChanged,
  mergeMap,
  asapScheduler,
  first,
  map,
  timer,
  Subscription,
  combineLatest,
  share,
  switchMap,
  of,
} from 'rxjs';
import { AuthInfoLike, AuthInfoModel, RoleLike } from '../models/auth.model';
import jwt_decode from 'jwt-decode';
import { IRxStorage } from '@rxjs-storage/core/dist/types/internal/interfaces';
import { RxArcgisPortalAuthService } from '@rxjs-arcgis-auth/core';
import { AppConfigService } from './app-config.service';
import { RoleType, SiiaPermissionType } from '../models/role-type';
import {
  siiaPermissionsRolesMap,
  siiaRolesMap,
} from '../constants/siia-roles.constant';
import { ArrayUtils } from '@utils/array.utils';

export const AUTH_STORAGE = new InjectionToken<IRxStorage>('AUTH_STORAGE');

const authInfoKey = 'auth:info';

@Injectable()
export class AuthService {
  private _authInfoObservable: Observable<AuthInfoModel>;
  private _entityIdObservable: Observable<string>;
  private _rolesObservable: Observable<RoleLike[]>;

  private _authenticatedObservable: Observable<boolean>;
  private _authenticatedEsriObservable: Observable<boolean>;

  constructor(
    private http: HttpClient,
    @Inject(AUTH_STORAGE) private storage: IRxStorage,
    private arcgisPortalAuthService: RxArcgisPortalAuthService,
    private appConfig: AppConfigService
  ) {}

  requireEsriAuthInfoAsync(): Promise<any> {
    return this.arcgisPortalAuthService
      .requireAuthInfo()
      .pipe(
        mergeMap((esriAuthInfo) => {
          return this.http
            .post<any>(`~auth/LoginEsri`, {
              user: esriAuthInfo.username,
              token: esriAuthInfo.token,
            })
            .pipe(
              map((res) => {
                return this._handleLogin(
                  { userLogin: res.userLogin },
                  esriAuthInfo.userId,
                  'ANACOM'
                );
              })
            );
        })
      )
      .toPromise();
  }

  logInByToken(token: string): Observable<AuthInfoModel> {
    return this.http
      .post<any>(`~auth/validateTokenSiia`, null, { params: { token } })
      .pipe(
        map((res) => {
          return this._handleLogin(res);
        })
      );
  }

  logOut() {
    if (this.appConfig.internalMode) {
      this.arcgisPortalAuthService.destroyCredentials();
    }

    this.storage.removeItem(authInfoKey);
  }

  getEsriCredentials() {
    return this.getAuthInfo().pipe(map((x) => x?.esriCredentials));
  }

  getEsriToken() {
    if (this.appConfig.internalMode) {
      return this.arcgisPortalAuthService.getToken();
    }

    return this.getEsriCredentials().pipe(map((x) => x?.token));
  }

  getUserName() {
    if (this.appConfig.internalMode) {
      return this.arcgisPortalAuthService
        .getAuthInfo()
        .pipe(map((x) => x?.username));
    }

    return this.getAuthInfo().pipe(map((x) => x?.userName));
  }

  getInitialMapConfig() {
    return this.getAuthInfo().pipe(map((x) => x?.initialMapConfig));
  }

  getToken() {
    return this.getAuthInfo().pipe(map((x) => x?.accessToken));
  }

  getUserId() {
    return this.getAuthInfo().pipe(map((x) => x?.userId));
  }

  getEntityId() {
    return this.observeEntityId().pipe(first());
  }

  observeEntityId() {
    if (!this._entityIdObservable) {
      this._entityIdObservable = this.observeAuthInfo().pipe(
        map((x) => x?.entityId),
        distinctUntilChanged()
      );
    }

    return this._entityIdObservable;
  }

  getAuthInfo(): Observable<AuthInfoModel> {
    return this.observeAuthInfo().pipe(first());
  }

  isEsriAuthenticated() {
    return this.observeEsriAuthenticated().pipe(first());
  }

  observeEsriAuthenticated() {
    if (!this._authenticatedEsriObservable) {
      const isAuthenticatedFn = (expires: number) => {
        return expires > Date.now();
      };

      const observable = new Observable<boolean>((subscriber) => {
        let subscription: Subscription;

        subscriber.add(() => {
          subscription?.unsubscribe();
        });
        subscriber.add(
          combineLatest(
            this.appConfig.internalMode
              ? this.arcgisPortalAuthService
                  .listenAuthInfo()
                  .pipe(map((x) => x?.expiresAt ?? 0))
              : this.observeAuthInfo().pipe(
                  map((x) => x?.esriCredentials?.expires ?? 0)
                ),
            this.observeAuthenticated()
          ).subscribe(([expires, authenticated]) => {
            subscription?.unsubscribe();

            const esriAuthenticated =
              authenticated && isAuthenticatedFn(expires);
            if (esriAuthenticated) {
              const delay = expires - Date.now();
              subscription = timer(delay, 1000).subscribe(() => {
                if (!isAuthenticatedFn(expires)) {
                  subscription?.unsubscribe();
                  subscriber.next(false);
                }
              });
            }

            subscriber.next(esriAuthenticated);
          })
        );
      });

      this._authenticatedEsriObservable = observable.pipe(
        distinctUntilChanged(),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
    }

    return this._authenticatedEsriObservable;
  }

  observeHasSiiaPermission(
    siiaPermissionType: SiiaPermissionType
  ): Observable<boolean> {
    const aux = siiaPermissionsRolesMap.get(siiaPermissionType);
    const roleIdsAllowed = aux?.allowed;
    const roleIdsNotAllowed = aux?.notallowed;

    const observables = new Array<Observable<boolean>>();
    if (roleIdsAllowed?.length) {
      observables.push(this.observeHasAnyRoles(roleIdsAllowed));
    }

    if (roleIdsNotAllowed?.length) {
      observables.push(this.observeHasNotAllRoles(roleIdsNotAllowed));
    }

    return combineLatest(observables).pipe(
      map((res) => {
        return res.every((x) => x);
      }),
      distinctUntilChanged()
    );
  }

  observeHasNotAnyRoleTypes(
    roleTypes: RoleType | RoleType[]
  ): Observable<boolean> {
    return this.observeHasAnyRoleTypes(roleTypes).pipe(map((x) => !x));
  }

  observeHasAnyRoleTypes(
    roleTypes: RoleType | RoleType[]
  ): Observable<boolean> {
    if (!Array.isArray(roleTypes)) {
      roleTypes = [roleTypes];
    }

    const roleIds = roleTypes.flatMap((type) => {
      return siiaRolesMap.get(type) ?? [];
    });

    return this.observeHasAnyRoles(roleIds);
  }

  observeHasNotOnlyAnyOfThisRoleTypes(
    roleTypes: RoleType | RoleType[]
  ): Observable<boolean> {
    return this.observeHasOnlyAnyOfThisRoleTypes(roleTypes).pipe(
      map((x) => !x)
    );
  }

  observeHasOnlyAnyOfThisRoleTypes(
    roleTypes: RoleType | RoleType[]
  ): Observable<boolean> {
    if (!Array.isArray(roleTypes)) {
      roleTypes = [roleTypes];
    }

    const roleIds = roleTypes.flatMap((type) => {
      return siiaRolesMap.get(type) ?? [];
    });

    return this.observeHasOnlyAnyOfThisRoles(roleIds);
  }

  observeHasNotAllRoles(roleIds: string | string[]): Observable<boolean> {
    return this.observeHasAllRoles(roleIds).pipe(map((x) => !x));
  }

  observeHasAllRoles(roleIds: string | string[]): Observable<boolean> {
    return this.observeHasRoles(roleIds).pipe(
      map((res) => {
        for (const value of res.values()) {
          if (!value) {
            return false;
          }
        }

        return true;
      }),
      distinctUntilChanged()
    );
  }

  observeHasNotAnyRoles(roleIds: string | string[]): Observable<boolean> {
    return this.observeHasAnyRoles(roleIds).pipe(map((x) => !x));
  }

  observeHasAnyRoles(roleIds: string | string[]): Observable<boolean> {
    return this.observeHasRoles(roleIds).pipe(
      map((res) => {
        for (const value of res.values()) {
          if (value) {
            return true;
          }
        }

        return false;
      }),
      distinctUntilChanged()
    );
  }

  observeHasOnlyAnyOfThisRoles(
    roleIds: string | string[]
  ): Observable<boolean> {
    if (!Array.isArray(roleIds)) {
      roleIds = [roleIds];
    } else {
      roleIds = roleIds.slice();
    }

    return this.observeRoles().pipe(
      map((roles) => {
        if (!roles?.length) {
          return false;
        }

        for (const role of roles) {
          if (!roleIds.includes(role.id)) {
            return false;
          }
        }

        return true;
      }),
      distinctUntilChanged()
    );
  }

  observeHasRoles(
    roleIds: string | string[]
  ): Observable<Map<string, boolean>> {
    if (!Array.isArray(roleIds)) {
      roleIds = [roleIds];
    }

    roleIds = ArrayUtils.uniqBy(roleIds);

    return this.observeRoles().pipe(
      map((roles) => {
        const res = (roleIds as string[]).reduce((acc, roleId) => {
          const aBool = !!roles?.some((x) => x?.id === roleId);
          acc.set(roleId, aBool);
          return acc;
        }, new Map<string, boolean>());

        return res;
      }),
      distinctUntilChanged((prev, curr) => {
        for (const [key, value] of prev) {
          if (curr.get(key) !== value) {
            return false;
          }
        }

        return true;
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
      map((res) => {
        return new Map<string, boolean>(res);
      })
    );
  }

  observeRoles() {
    if (!this._rolesObservable) {
      this._rolesObservable = this.observeAuthInfo().pipe(
        map((x) => {
          return x?.roles;
        }),
        distinctUntilChanged((prev, curr) => {
          if (prev?.length !== curr?.length) {
            return false;
          }

          for (const role of curr) {
            if (!prev.some((x) => x.id === role.id)) {
              return false;
            }
          }

          return true;
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
    }

    return this._rolesObservable;
  }

  observeAuthenticated(): Observable<boolean> {
    if (!this._authenticatedObservable) {
      const isAuthenticatedFn = (authInfo: AuthInfoModel) => {
        return authInfo?.isAuthenticated() ?? false;
      };

      const observable = new Observable<boolean>((subscriber) => {
        let subscription: Subscription;

        subscriber.add(() => {
          subscription?.unsubscribe();
        });
        subscriber.add(
          this.observeAuthInfo().subscribe((authInfo) => {
            subscription?.unsubscribe();

            const authenticated = isAuthenticatedFn(authInfo);
            if (authenticated) {
              const delay = authInfo.expires!.getTime() - Date.now();
              subscription = timer(delay, 1000).subscribe(() => {
                if (!isAuthenticatedFn(authInfo)) {
                  subscription?.unsubscribe();
                  this.storage.removeItem(authInfoKey);
                }
              });
            }

            subscriber.next(authenticated);
          })
        );
      });

      this._authenticatedObservable = observable.pipe(
        distinctUntilChanged(),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
    }

    return this._authenticatedObservable;
  }

  observeAuthInfo(): Observable<AuthInfoModel> {
    if (!this._authInfoObservable) {
      const observable = new Observable<AuthInfoModel>((subscriber) => {
        const authInfoLike: AuthInfoLike = this.storage.getItem(authInfoKey);
        let authInfo = new AuthInfoModel(authInfoLike);

        if (!authInfo?.isAuthenticated()) {
          authInfo = null;
        }

        subscriber.next(authInfo);
        subscriber.complete();
      });

      this._authInfoObservable = concat(
        observable,
        merge(
          this.storage.onItemChanged(authInfoKey),
          this.storage.onItemRemoved(authInfoKey)
        ).pipe(mergeMap(() => observable))
      ).pipe(
        distinctUntilChanged(),
        shareReplay(1, Number.POSITIVE_INFINITY, asapScheduler)
      );
    }

    return this._authInfoObservable;
  }

  private _handleLogin(
    {
      userLogin,
      esriCredentials,
      tokenSiia,
    }: { userLogin: any; esriCredentials?: any; tokenSiia?: any },
    userId?: string,
    entityId?: string
  ): AuthInfoModel {
    const accessToken = userLogin.accessToken;
    const jwtDecoded = jwt_decode<any>(accessToken);

    const siiaObj = tokenSiia?.result?.esri_UtilizadorSIIAid;

    const initialCenter = siiaObj?.parentcustomerid_account;

    const authInfoLike: AuthInfoLike = {
      accessToken,
      expires: new Date(jwtDecoded.exp * 1000),
      esriCredentials,
      userId: userId ?? siiaObj?.contactid,
      entityId: entityId ?? siiaObj?._parentcustomerid_value,
      roles: tokenSiia?.result?.roles,
      initialMapConfig: {
        center: {
          latitude: initialCenter?.esri_latitude,
          longitude: initialCenter?.esri_longitude,
        },
        scale: 4500,
      },
    };

    const authInfo = new AuthInfoModel(authInfoLike);
    this.storage.setItem(authInfoKey, authInfo);
    return authInfo;
  }
}
